import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { LocalStorageKeys, getTransformedDataForPagination, objectToQueryParams } from '../../../utils';

export const searchPromptsAPI = createApi({
    reducerPath: "searchPromptsAPI",
    baseQuery: retry(fetchBaseQuery({
        baseUrl: config.api_url + "ai/search_prompt",
        prepareHeaders: (headers, { getState }) => {
            const token = localStorage.getItem(LocalStorageKeys.authToken);

            // If we have a token set in state, let's assume that we should be passing it.
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }

            return headers
        },
    }), { maxRetries: 1 }),
    tagTypes: ['Search Prompts', 'Search Prompt by Id'],
    endpoints: (builder) => ({

        // Get Search Prompts API
        getSearchPrompts: builder.query({
            query: (payload: {
                search?: string,
                page: number,
                pageSize: number,
                object_id?: string,
                object_type?: string,
                sort_by?: string,
                sort_order?: string,
                include_assoc?: string[]
            }) => ({
                url: objectToQueryParams(payload),
                method: "GET"
            }),
            transformResponse: (data: any, meta: { request: Request; response?: Response }) => {
                return getTransformedDataForPagination(data, meta?.response);
            },
            providesTags: ["Search Prompts"],
        }),

        // Get Search Prompts by ID API
        getSearchPromptById: builder.query({
            query: (id) => ({ url: `/${id}`, method: "GET" }),
            providesTags: ["Search Prompt by Id"],
        }),

        // Create Search Prompt API
        createSearchPrompt: builder.mutation({
            query: (payload: any) => ({ method: "POST", body: payload }),
            invalidatesTags: ["Search Prompts"],
        }),

        // Update Search Prompt API
        updateSearchPrompt: builder.mutation({
            query: (payload: { id: string, payload: any }) => ({ url: `/${payload.id}`, method: "PUT", body: payload.payload }),
            invalidatesTags: ["Search Prompts"]
        }),

        // Delete InstantSearch API
        deleteSearchPrompt: builder.mutation({
            query: (id) => ({ url: `/${id}`, method: "DELETE" }),
            invalidatesTags: ["Search Prompts"]
        })
      
    })
})

// Export hooks for usage in functional components
export const {
   useGetSearchPromptsQuery, useLazyGetSearchPromptsQuery, useGetSearchPromptByIdQuery, useLazyGetSearchPromptByIdQuery, useCreateSearchPromptMutation, useUpdateSearchPromptMutation, useDeleteSearchPromptMutation
} = searchPromptsAPI
